import http from 'hub-http/clients/apiClient';
import { dataFetchingClient } from '../../utils/DataFetchingClient';
import { registerQuery, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { quickFetchOrGet, getCachedThenNew } from '../../utils/ajaxUtils';
import { ACADEMY_CATALOG_SERVICE_URI, ACADEMY_CATALOG_SERVICE_URI_V2 } from '../../constants/ApiConstants';
import getLang from 'LearningCenterUI/utils/getLang';
import { useMemo } from 'react';
import { updateFullLessonCache, updatePartialLessonCache } from '../lessons/lessonQueries';
import { useIsPartner } from '../partners/partnersQueries';
import pick from 'hs-lodash/pick';
const normalizeLesson = lesson => Object.assign({
  contentIds: lesson.content.map(content => content.id),
  isRequesting: false,
  hasError: false
}, pick(lesson, ['deprecationCta', 'deprecationFinalDate', 'deprecationMessage', 'deprecationWarningDate', 'description', 'duration', 'hsSoftwareFilters', 'id', 'internalName', 'language', 'lessonType', 'levelFilters', 'name', 'previewThumbnailRectangle', 'previewThumbnailRectangleUrl', 'previewThumbnailSquare', 'previewThumbnailSquareUrl', 'subCategories', 'visibility']));
export const GET_TRACK = registerQuery({
  fieldName: 'track',
  args: ['trackId', 'language'],
  fetcher({
    trackId,
    language
  }) {
    return quickFetchOrGet(`${ACADEMY_CATALOG_SERVICE_URI}/tracks/${trackId}/${language}`).then(response => response);
  }
});
export const fetchTrackById = (trackId, language) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_TRACK,
    variables: {
      trackId,
      language: language.toLowerCase()
    }
  }).then(track => {
    track.lessons.forEach(lesson => {
      updateFullLessonCache(lesson, language);
      updatePartialLessonCache(normalizeLesson(lesson), language);
    });
    return track;
  });
};
export const useFullTrack = (trackId, language, skip) => {
  if (!language || !language.length) language = getLang();
  const {
    data: track,
    loading: trackLoading,
    error: trackError
  } = useQuery(GET_TRACK, {
    variables: {
      trackId: trackId === null || trackId === void 0 ? void 0 : trackId.toString(),
      language: language.toLowerCase()
    },
    skip: skip || trackId === null || isNaN(trackId),
    onCompleted(data) {
      data.track.lessons.forEach(lesson => {
        updateFullLessonCache(lesson, language);
        updatePartialLessonCache(normalizeLesson(lesson), language);
      });
    }
  });
  return {
    track,
    trackLoading,
    trackError
  };
};
export const useTrackLessons = (trackId, language) => {
  const {
    track,
    trackLoading,
    trackError
  } = useFullTrack(Number(trackId), language);
  return {
    trackLessons: track ? track.lessons : null,
    trackLessonsLoading: trackLoading,
    trackLessonsError: trackError
  };
};
export const useTrackPracticalExercises = (trackId, language) => {
  const {
    trackLessons,
    trackLessonsLoading,
    trackLessonsError
  } = useTrackLessons(trackId, language);
  const trackPracticalExercises = useMemo(() => {
    if (!trackLessons) return null;
    const practicalExercises = [];
    trackLessons.forEach(lesson => lesson.content.forEach(content => {
      if (content.contentType === 'PRACTICAL_EXERCISE_FLOW') {
        practicalExercises.push(Object.assign({}, content, {
          parentLesson: lesson
        }));
      }
    }));
    return practicalExercises;
  }, [trackLessons]);
  return {
    trackPracticalExercises,
    trackPracticalExercisesLoading: trackLessonsLoading,
    trackPracticalExercisesError: trackLessonsError
  };
};
export const GET_TRACK_AVAILABLE_LANGUAGES = registerQuery({
  fieldName: 'trackAvailableLanguages',
  args: ['trackId'],
  fetcher({
    trackId
  }) {
    return http.get(`${ACADEMY_CATALOG_SERVICE_URI}/tracks/${trackId}/available-languages`).then(response => response).then(data => data.map(lang => lang.toUpperCase()));
  }
});
export const fetchAvailableLanguagesByTrackId = trackId => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_TRACK_AVAILABLE_LANGUAGES,
    variables: {
      trackId
    }
  });
};
export const useAvailableLanguagesByTrack = trackId => {
  const {
    data: trackLanguages,
    loading: trackLanguagesLoading,
    error: trackLanguagesError
  } = useQuery(GET_TRACK_AVAILABLE_LANGUAGES, {
    variables: {
      trackId
    }
  });
  return {
    trackLanguages,
    trackLanguagesLoading,
    trackLanguagesError
  };
};
export const GET_ALL_PUBLIC_TRACKS = registerQuery({
  fieldName: 'allPublicTracks',
  args: ['language'],
  fetcher({
    language
  }) {
    const lang = language || getLang();
    return getCachedThenNew(`tracks-${lang}`, `${ACADEMY_CATALOG_SERVICE_URI_V2}/tracks/${lang}?fallbackLanguage=en`).then(response => response);
  }
});
export const useAllPublicTracks = (language, skip = false) => {
  const {
    data: allPublicTracks,
    loading: allPublicTracksLoading,
    error: allPublicTracksError
  } = useQuery(GET_ALL_PUBLIC_TRACKS, {
    variables: {
      language: language.toLowerCase()
    },
    skip
  });
  return {
    allPublicTracks: allPublicTracks ? allPublicTracks.result : null,
    allPublicTracksLoading,
    allPublicTracksError
  };
};
const GET_ALL_PARTNER_TRACKS = registerQuery({
  fieldName: 'allPartnerTracks',
  args: ['language'],
  fetcher({
    language
  }) {
    return quickFetchOrGet(`partner-tracks-${language}`, `${ACADEMY_CATALOG_SERVICE_URI}/tracks/partners/${language}?fallbackLanguage=en`).then(response => {
      return response;
    });
  }
});
export const fetchAllPartnerTracks = ({
  language
}) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_ALL_PARTNER_TRACKS,
    variables: {
      language: language.toLowerCase()
    }
  });
};
export const useAllPartnerTracks = (language, skip = false) => {
  const isPartner = useIsPartner();
  const {
    data: allPartnerTracks,
    loading: allPartnerTracksLoading,
    error: allPartnerTracksError
  } = useQuery(GET_ALL_PARTNER_TRACKS, {
    variables: {
      language: language.toLowerCase()
    },
    skip: !isPartner || skip
  });
  return {
    allPartnerTracks,
    allPartnerTracksLoading,
    allPartnerTracksError
  };
};
export const useAllTracks = (language = getLang(), skip = false) => {
  const {
    allPublicTracks,
    allPublicTracksLoading,
    allPublicTracksError
  } = useAllPublicTracks(language, skip);
  const {
    allPartnerTracks,
    allPartnerTracksLoading,
    allPartnerTracksError
  } = useAllPartnerTracks(language, skip);
  const allTracks = useMemo(() => {
    const allTracksArray = [...(Array.isArray(allPublicTracks) ? allPublicTracks : []), ...(Array.isArray(allPartnerTracks) ? allPartnerTracks : [])];
    return allTracksArray.reduce((currentTracks, track) => {
      currentTracks[track.id] = track;
      return currentTracks;
    }, {});
  }, [allPartnerTracks, allPublicTracks]);
  return {
    allTracks,
    allTracksLoading: allPublicTracksLoading || allPartnerTracksLoading,
    allTracksError: allPublicTracksError || allPartnerTracksError
  };
};
export const useTrack = (trackId, language = getLang(), skip = false) => {
  if (!language || !language.length) language = getLang();
  const {
    allTracks,
    allTracksLoading: trackLoading,
    allTracksError: trackError
  } = useAllTracks(language, skip);
  return {
    track: allTracks && allTracks[trackId],
    trackLoading,
    trackError
  };
};